// Cross-sell between life and general insurance (idea 45): four explainable situations found in the client's own
// records (insured items of schema 8 + policies + household). They are conversation starters for the advisor —
// never advice, never a claim that the client lacks cover (cover held elsewhere is not in the CRM), and nothing is
// sent: the matching campaign theme carries a polite, neutral draft (el + en).
//
//  property_no_earthquake   an active property with no linked active general policy; when a linked policy exists but
//                           its wording (product, notes, tags, benefits) does not mention earthquake, the person is
//                           listed as INCOMPLETE (cover detail not recorded), never as a member
//  family_vehicle_no_life   an active vehicle, a family (spouse / partner or children in the household) and no active
//                           life policy (life or credit life) owned or insured
//  mortgage_no_protection   an active property marked mortgaged and no active credit-life or life policy
//  business_no_liability    an active business with employees > 0 and no linked active liability policy; employees not
//                           recorded → INCOMPLETE
import type { WorkspaceView } from './changeset.ts';
import { householdOf } from './household.ts';
import { policyCategory } from './policy-engine.ts';
import { coverageForPerson } from './person-cover.ts';
import type { Day } from './dates.ts';
import { isActivePolicy, searchKey } from './rules.ts';
import type { InsuredItem, Person, Policy, PolicyCategory } from './schema.ts';

export const CROSS_SELL_SEGMENTS = ['property_no_earthquake', 'family_vehicle_no_life', 'mortgage_no_protection', 'business_no_liability'] as const;
export type CrossSellSegment = typeof CROSS_SELL_SEGMENTS[number];
export interface CrossSellHit { personId: string; segment: CrossSellSegment; incomplete: boolean; item: InsuredItem | null; detail: Record<string, string | number | boolean> }

const GENERAL: readonly PolicyCategory[] = ['motor', 'home', 'travel', 'liability', 'business'];
const isGeneral = (p: Policy) => p.branch === 'general' || GENERAL.includes(policyCategory(p));
const EARTHQUAKE = /σεισμ|earthquake|seismic|quake/;
/** the policy's own wording mentions earthquake cover (product, notes, tags, benefit kinds and notes) */
export function mentionsEarthquake(p: Policy): boolean {
  const text = [p.productName, p.notes, ...(p.tags ?? []), ...(p.benefits ?? []).flatMap((b) => [b.kind, b.note])].filter(Boolean).join(' ');
  return EARTHQUAKE.test(searchKey(text));
}
const linkedLive = (v: WorkspaceView, item: InsuredItem) => (item.policyIds ?? []).map((id) => v.get('policies', id)).filter((p): p is Policy => !!p && isActivePolicy(p));

/** every cross-sell situation in the book (or for one person) */
export function crossSellHits(v: WorkspaceView, today: Day, opts: { personId?: string; segment?: CrossSellSegment } = {}): CrossSellHit[] {
  const out: CrossSellHit[] = [];
  const want = (s: CrossSellSegment) => !opts.segment || opts.segment === s;
  const persons = opts.personId ? [v.get('persons', opts.personId)].filter((p): p is Person => !!p) : v.rows('persons');
  for (const person of persons) {
    if (person.archivedAt || person.stage === 'dependent') continue;
    const items = v.where('insured_items', 'personId', person.id).filter((i) => i.status === 'active');
    if (!items.length) continue;
    // the person's own life cover today (as an insured person, coverageForPerson), general-insurance policies left out
    const cover = coverageForPerson(v, person.id, today).filter((c) => !isGeneral(c.policy));
    const hasCat = (cats: PolicyCategory[]) => cover.some((c) => c.categories.some((x) => cats.includes(x)));
    const props = items.filter((i) => i.kind === 'property');
    if (want('property_no_earthquake')) {
      for (const it of props) {
        const general = linkedLive(v, it).filter(isGeneral);
        if (!general.length) { out.push({ personId: person.id, segment: 'property_no_earthquake', incomplete: false, item: it, detail: { item: it.label, linkedPolicy: false } }); break; }
        if (!general.some(mentionsEarthquake)) { out.push({ personId: person.id, segment: 'property_no_earthquake', incomplete: true, item: it, detail: { item: it.label, linkedPolicy: true } }); break; }
      }
    }
    if (want('family_vehicle_no_life')) {
      const vehicles = items.filter((i) => i.kind === 'vehicle');
      const family = householdOf(v, person.id).filter((m) => m.person.id !== person.id && (m.role === 'spouse' || m.role === 'child'));
      if (vehicles.length && family.length && !hasCat(['life', 'credit_life'])) out.push({ personId: person.id, segment: 'family_vehicle_no_life', incomplete: false, item: vehicles[0]!, detail: { item: vehicles[0]!.label, vehicles: vehicles.length, family: family.length } });
    }
    if (want('mortgage_no_protection')) {
      const mortgaged = props.find((i) => i.mortgaged === true);
      if (mortgaged && !hasCat(['credit_life', 'life'])) out.push({ personId: person.id, segment: 'mortgage_no_protection', incomplete: false, item: mortgaged, detail: { item: mortgaged.label } });
    }
    if (want('business_no_liability')) {
      for (const it of items.filter((i) => i.kind === 'business')) {
        if (linkedLive(v, it).some((p) => policyCategory(p) === 'liability')) continue;
        if (it.employees == null) { out.push({ personId: person.id, segment: 'business_no_liability', incomplete: true, item: it, detail: { item: it.label, employees: false } }); break; }
        if (it.employees > 0) { out.push({ personId: person.id, segment: 'business_no_liability', incomplete: false, item: it, detail: { item: it.label, employees: it.employees } }); break; }
      }
    }
  }
  return out;
}
